import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { usePlans } from '../utils/usePlans';
import { fetchPayments, downloadPlanCsv, downloadCompanyCsv } from '../api/payment';
import './PaymentInfoPage.css';
import { NoticeModal } from '../components/Modals';

const PaymentInfoPage = () => {
  const { oneTimeOptions, subscriptionOptions, loading } = usePlans();
  const { companyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeMsg, setNoticeMsg] = useState('');

  const showNotice = (msg) => {
    setNoticeMsg(msg);
    setNoticeOpen(true);
  };

  const [companyName] = useState(
    location.state?.companyName || `企業ID ${companyId}`
  );
  const [paymentInfo, setPaymentInfo] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);

  const planLabelMap = {};
  [...oneTimeOptions, ...subscriptionOptions].forEach((option) => {
    if (option.price_id) planLabelMap[option.price_id] = option.name;
  });

  useEffect(() => {
    const getPayments = async () => {
      try {
        const data = await fetchPayments(companyId);
        const grouped = data.reduce((acc, payment) => {
          const price_id = payment.price_id || 'unknown';
          if (!acc[price_id]) acc[price_id] = { price_id, payments: [] };
          acc[price_id].payments.push(payment);
          return acc;
        }, {});
        setPaymentInfo(Object.values(grouped));
        setErrorMessage(null);
      } catch (error) {
        console.error('支払い情報の取得に失敗:', error);
        setPaymentInfo([]);
        setErrorMessage('支払い情報の取得に失敗しました。');
      }
    };
    getPayments();
  }, [companyId]);

  const getMembershipStatus = (status) => {
    switch (status) {
      case 0:
        return '契約前';
      case 1:
        return '契約中';
      case 2:
        return '停止';
      default:
        return '不明';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };

  const downloadPlanCSV = async (plan) => {
    if (!plan.payments || plan.payments.length === 0) {
      showNotice('ダウンロード可能なデータがありません');
      return;
    }

    try {
      const blob = await downloadPlanCsv(companyId, plan.price_id);
      const url = URL.createObjectURL(blob);

      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const planName = plan.payments[0]?.plan_name ?? '';
      const fileName = `${companyName}${planName}支払い情報${dateStr}.csv`;

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSVダウンロードエラー:', error);
      alert('CSVダウンロードに失敗しました');
    }
  };

  const downloadAllCSV = async () => {
    if (paymentInfo.length === 0) {
      showNotice('ダウンロード可能なデータがありません');
      return;
    }

    try {
      const blob = await downloadCompanyCsv(companyId);
      const url = URL.createObjectURL(blob);

      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${companyName}支払い情報${dateStr}.csv`;

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSVダウンロードエラー:', error);
      alert('CSVダウンロードに失敗しました');
    }
  };

  if (loading) return <div>プランデータを読み込み中...</div>;

  return (
    <div className="paymentInfoBody">
      <h2 className="paymentTitle">{companyName} の支払い情報</h2>
      {errorMessage && <p className="error">{errorMessage}</p>}

      {paymentInfo.length > 0 ? (
        <>
          <div className="paymentInfoScroll">
            {paymentInfo.map((plan, idx) => (
              <div key={plan.price_id || idx} className="planSection">
                <div className="planTitle">
                  <h2>
                    {planLabelMap[plan.price_id] || `プラン名：${plan.name}`}
                  </h2>
                  <button
                    className="csvButton"
                    onClick={() => downloadPlanCSV(plan)}
                  >
                    プランのCSV ダウンロード
                  </button>
                </div>
                {plan.payments.length > 0 ? (
                  plan.payments.map((p) => (
                    <div key={p.checkout_session_id} className="paymentRecord">
                      <p>
                        <strong>キャンペーンコード：</strong>{' '}
                        {p.campaign_code || 'なし'}
                      </p>
                      <p>
                        <strong>会員ID：</strong>{' '}
                        {p.member_id}
                      </p>
                      <p>
                        <strong>決済番号：</strong>{' '}
                        {p.linkage_identifier}
                      </p>
                      <p>
                        <strong>契約日時：</strong>{' '}
                        {formatDate(p.created_at)}
                      </p>
                      <p>
                        <strong>初回決済：</strong>{' '}
                        {formatDate(p.first_payment_day)}
                      </p>
                      <p>
                        <strong>次回決済番号：</strong>{' '}
                        {formatDate(p.next_payment_day)}
                      </p>
                      <p>
                        <strong>契約状況：</strong>{' '}
                        {getMembershipStatus(p.is_member)}
                      </p>
                      <p>
                        <strong>クーポン番号：</strong>{' '}
                        {p.coupon_code || 'なし'}
                      </p>
                      <p>
                        <strong>クーポン終了予定日：</strong>{' '}
                        {formatDate(p.coupon_end_date) || 'なし'}
                      </p>
                      <p>
                        <strong>クーポン強制停止日：</strong>{' '}
                        {p.force_stop_day ? formatDate(p.force_stop_day) : 'なし'}
                      </p>
                      <p>
                        <strong>キャンセル日時：</strong>{' '}
                        {p.end_day ? formatDate(p.end_day) : 'なし'}
                      </p>
                      <hr />
                    </div>
                  ))
                ) : (
                  <p>支払い情報がありません</p>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <p>支払い情報がありません</p>
      )}
      <div className="paymentInfoButtons">
        <button className="paymentInfoButton1" onClick={downloadAllCSV}>
          企業のCSV ダウンロード
        </button>
        <button
          className="paymentInfoButton2"
          onClick={() => navigate('/adminpage')}
        >
          管理画面に戻る
        </button>
      </div>
      <NoticeModal
        open={noticeOpen}
        message={noticeMsg}
        onClose={() => setNoticeOpen(false)}
      />
    </div>
  );
};

export default PaymentInfoPage;
